import { queryOptions, useQuery } from '@tanstack/react-query';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { EnvironmentVariables } from '../EnvironmentVariables';
import { useAppSelector } from '@/store';
import { SiteDetails } from '@/reducers/jbpGeneralSlice';
import { useEffect } from 'react';

export enum RestTypes {
  Get = 'GET',
  Put = 'PUT',
  Post = 'POST',
  Delete = 'DELETE',
  Patch = 'PATCH'
}

export const contractTypeCodes: Record<string, string> = {
  HH: '8',
  NHH: '2',
  SUBMETER: '4',
  DNO: '100',
  BNO: '101',
  MSS: '111',
  MSA: '112'
};

export const getSupplierIdForHeader = (queryPath: string, site?: SiteDetails, userAssociatedMpids?: string[]): string => {
  const siteAssociatedMpid = site?.userMpid;

  if (!siteAssociatedMpid || queryPath.includes('/site')) {
    let listOfMPIDs: string[] = [];
    if (userAssociatedMpids) {
      listOfMPIDs = userAssociatedMpids;
    } else {
      listOfMPIDs = [];
    }
    const ValidMPIDSForCurrentUser = listOfMPIDs.join(',');
    return ValidMPIDSForCurrentUser;
  } else {
    return siteAssociatedMpid != null ? siteAssociatedMpid : '';
  }
};

//This can be split to method specific handling in the future if needed
//For the time being this was all duplicated so moved to this function.
const handleAxiosErrorResponse = (err: AxiosError) => {
  const redirectUrl: string = EnvironmentVariables.RedirectUrl;
  if (err.response?.status === 401 || err.response?.status === 403) {
    console.log(`Navigating to ${redirectUrl} for expired auth`);
    window.location.href = redirectUrl;
  } else if (err.code == 'ERR_BAD_REQUEST' && err.response == undefined) {
    throw { msg: 'The server was unable to respond', code: err.status };
  } else {
    throw err.response?.data;
  }
};

const codeShouldPreventRetry = (code: number | undefined) => {
  return code == 404;
};

export const useJbpQuery = <T, E>(
  restType: RestTypes,
  data: any,
  queryIdentifiers: string[],
  queryPath: string,
  enabled: boolean,
  serviceLine: string,
  onSuccess?: (data: T) => void,
  onError?: (err: E) => void,
  cacheTime = 0,
  staleTime = 0
) => {
  const basePath = EnvironmentVariables.ApiUrl;
  const restTypeIsMutation = restType == RestTypes.Put || restType == RestTypes.Post || restType == RestTypes.Patch;

  const contractType = contractTypeCodes[serviceLine];

  const { site, userAssociatedMpids, contractId } = useAppSelector(state => state.jbp);
  const mpid = getSupplierIdForHeader(queryPath, site, userAssociatedMpids);

  const mpidIsReady = userAssociatedMpids !== undefined || site?.userMpid !== undefined;

  const config: AxiosRequestConfig = {
    withCredentials: true,
    headers: {
      'Contract-Type': contractType,
      MPID: mpid,
      'Contract-ID': contractId
    }
  };

  const getParameterisedPath = (data: any, queryPath: string) => {
    const params = new URLSearchParams(data).toString();

    if (params == undefined || params == '') {
      return queryPath;
    } else {
      return `${queryPath}?${params}`;
    }
  };

  const body = restTypeIsMutation ? data : undefined;
  const completeQuery = restTypeIsMutation ? queryPath : getParameterisedPath(data, queryPath);

  const getQueryFunction = (): any => {
    let query: (url: string, body?: any, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;
    const args: [string, any?] = [`${basePath}/${completeQuery}`];
    if (body) {
      args.push(body);
    }
    switch (restType) {
      case RestTypes.Get:
        query = axios.get<T>;
        break;
      case RestTypes.Put:
        query = axios.put<T>;
        break;
      case RestTypes.Post:
        query = axios.post<T>;
        break;
      case RestTypes.Delete:
        query = axios.delete<T>;
        break;
      case RestTypes.Patch:
        query = axios.patch<T>;
        break;
    }
    return () => {
      console.log(`Querying ${restType}: ${completeQuery}`);
      return query(...args, config)
        .then(response => response.data)
        .catch((err: AxiosError) => {
          handleAxiosErrorResponse(err);
        });
    };
  };

  const options = queryOptions<T, E>({
    queryKey: queryIdentifiers,
    queryFn: getQueryFunction(),
    retry: restTypeIsMutation ? false : (err: any) => codeShouldPreventRetry(err.code),
    refetchOnMount: restTypeIsMutation ? false : true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: restTypeIsMutation ? false : true,
    enabled: enabled && mpidIsReady,
    gcTime: cacheTime,
    staleTime
  });
  const response = useQuery<T, E>(options);

  useEffect(() => {
    if (response.isError) {
      onError && onError(response.error);
    }
    if (response.isSuccess) {
      onSuccess && onSuccess(response.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response.data, response.isError]);

  return response;
};

export const useGetUserInfoQuery = <T, E>(enabled: boolean, onSuccess?: (data: T) => void, onError?: (err: E) => void, cacheTime = 0, staleTime = 0) => {
  const basePath = EnvironmentVariables.ApiUrl;
  const queryPath = EnvironmentVariables.UserInfoPath;
  const queryIdentifiers: string[] = ['user-info'];

  const config: AxiosRequestConfig = {
    withCredentials: true,
    headers: {}
  };

  const query = (): any => {
    console.log(`Querying GET: ${queryPath}`);
    return axios
      .get<T>(`${basePath}/${queryPath}`, config)
      .then(data => {
        return data.data;
      })
      .catch((err: AxiosError) => {
        handleAxiosErrorResponse(err);
      });
  };

  const options = queryOptions<T, E>({
    queryFn: query,
    queryKey: queryIdentifiers,
    retry: (err: any) => codeShouldPreventRetry(err.code),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    enabled: enabled,
    gcTime: cacheTime,
    staleTime
  });
  const response = useQuery<T, E>(options);

  useEffect(() => {
    if (response.isError) {
      onError && onError(response.error);
    }
    if (response.isSuccess) {
      onSuccess && onSuccess(response.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response.data, response.isError]);

  return response;
};
